import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Paper, makeStyles, useTheme, List, ListItem, ListItemSecondaryAction, IconButton, ListItemText, Typography, useMediaQuery } from '@material-ui/core/';
import { themeTypeToBool, trophyIcons } from "../../../utils/helpers";
import { useTrail, animated } from 'react-spring';
import t from "../../../languages/pl";
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import TrophyLegendPopover from './TrophyLegendPopover';
import { connect } from 'react-redux';
import { TooltipTop } from "../../Common/CustomTooltips";

const useStyles = makeStyles(theme => ({
    container: {
        minWidth: 350,
        margin: theme.spacing(1),
        background: theme.palette.background.paper,
        position: 'relative'
    },
    title: {
        width: "85%",
        margin: "auto",
        padding: 15,
        paddingBottom: 5
    },
    list: {
        width: "80%",
        margin: "auto",
        paddingBottom: 30
    },
    listItem: {
        border: "solid",
        borderWidth: "1px 0 1px 0",
        borderColor: "#707070",
        justifyContent: "space-between"
    },
    disabledListItem: {
        background: '#B0B0B0',
        border: "solid",
        borderWidth: "1px 0 1px 0",
        borderColor: "#707070",
        justifyContent: "space-between"
    },
    listItemTrophy: {
        marginRight: 10
    },
    listHeader: {
        height: 35,
        fontWeight: 500,
        border: "solid",
        borderColor: "#707070",
        borderWidth: "0 0 1px 0",
        background: themeTypeToBool(theme.palette.type) ? "#F1F1F1" : "#43425D",
        justifyContent: "space-between"
    },
    disabledListHeader: {
        height: 35,
        fontWeight: 500,
        border: "solid",
        borderColor: "#707070",
        backgroundColor: 'gray',
        borderWidth: "0 0 1px 0",
        background: themeTypeToBool(theme.palette.type) ? "#F1F1F1" : "#43425D",
        justifyContent: "space-between"
    },
    helpIcon: {
        position: "absolute",
        top: 10,
        right: 10,
        padding: 0,
        opacity: 0.2,
        "&:hover": {
            opacity: 1,
        }
    },
    trophyIcon: {
        height: 40,
        width: 40,
        paddingLeft: theme.spacing(1.5),
        paddingTop: theme.spacing(1),
    },
    elementLabel: {
        cursor: 'pointer',
        "&:hover": {
            fontWeight: 600
        }
    },
    centeredText: {
        display: 'flex',
        justifyContent: 'center',
    },
    driverName: {
        whiteSpace: "nowrap",
        [theme.breakpoints.down('lg')]: {
            width: 150,
            maxWidth: 150
        },
        [theme.breakpoints.down('md')]: {
            width: 130,
            maxWidth: 130
        },
        [theme.breakpoints.down('sm')]: {
            width: 110,
            maxWidth: 110
        },
        [theme.breakpoints.down('xs')]: {
            width: 90,
            maxWidth: 90
        }
    },
    shorterDriverName: {
        whiteSpace: "nowrap",
        [theme.breakpoints.down('lg')]: {
            width: 110,
            maxWidth: 110
        },
    },
    kilometers: {
        maxWidth: 120,
        paddingLeft: 10,
        paddingRight: 10,
        textAlign: "center"
    }
}));

const setRowBackGround = (index, opacity) => {
    if (index === 0)
        return `rgba(190, 104, 237, ${opacity})`;
    else if (index === 1)
        return `rgba(190, 104, 237, ${opacity})`;
    else if (index === 2)
        return `rgba(190, 104, 237, ${opacity})`;
    else if (index === 3)
        return `rgba(190, 104, 237, ${opacity})`;
    else if (index === 4)
        return `rgba(190, 104, 237, ${opacity})`;
    else return "rgb(190, 104, 237)";
};

const initialData = [];

const animationConfig = { mass: 5, tension: 2000, friction: 200, duration: 2000 };

function TrophyTopList(props) {
    const theme = useTheme();
    const classes = useStyles(theme);
    const { title, data, attrName, headerLabel, onElementClick, isDrawerOpen } = props;
    const [gradeData, setGradeData] = useState(initialData);
    const [anchor, setAnchor] = useState(null);
    const isExtraSmallScreen = useMediaQuery(theme => theme.breakpoints.down('xs'));
    const isSmallScreen = useMediaQuery(theme => theme.breakpoints.down('sm'));
    const isMediumScreen = useMediaQuery(theme => theme.breakpoints.down('md'));
    const isLargeScreen = useMediaQuery(theme => theme.breakpoints.down('lg'));
    const [screen, setScreen] = useState("");
    const [listHeight, setListHeight] = useState(46);

    useEffect(() => {
        if(data.trofea === false){
            setListHeight(230);
        }
        else{
            setListHeight(46);
        }
    }, [data]);

    useEffect(() => {
        setScreen("xs");
    }, [isExtraSmallScreen]);

    useEffect(() => {
        setScreen("sm");
    }, [isSmallScreen]);

    useEffect(() => {
        setScreen("md");
    }, [isMediumScreen]);

    useEffect(() => {
        setScreen("lg");
    }, [isLargeScreen]);

    const driverDisplay = text => {
        if (screen === "xs") {
            if (text.length > 10)
                return text.slice(0, 10) + "...";
        }
        else if (screen === "sm") {
            if (text.length > 15)
                return text.slice(0, 15) + "...";
        }
        else if (screen === "md") {
            if (isDrawerOpen) {
                if (text.length > 17)
                    return text.slice(0, 15) + "...";
            }
            if (text.length > 20)
                return text.slice(0, 20) + "...";
        }
        else if (screen === "lg") {
            if (isDrawerOpen) {
                if (text.length > 17)
                    return text.slice(0, 15) + "...";
            }
            if (text.length > 25)
                return text.slice(0, 25) + "...";
        }
        return text;
    };

    const handleTrophyLegendShow = (e) => setAnchor(e.currentTarget);
    const trail = useTrail(data.trofea ? gradeData.length : 1, {
        config: animationConfig,
        opacity: 1,
        x: 0,
        height: listHeight,
        from: { opacity: 0, x: 20, height: 0 },
    });

    useEffect(() => {
        setGradeData(data.kierowcy);
    }, [data]);

    const listOneRow = (data, theme, index) => {
        if (data != null && data[attrName].length > 0) {
            let backgroundColor = setRowBackGround(index, (1 - index / 7), theme.palette.type);
            return (
                <ListItem key={data[attrName]} className={classes.listItem} style={{ background: backgroundColor }}>
                    <ListItemText className={isDrawerOpen && isLargeScreen ? classes.shorterDriverName : classes.driverName}>
                        <Typography
                            variant="body1" className={classes.elementLabel}
                            onClick={() => onElementClick(data)}
                        >
                            {driverDisplay(data[attrName]).includes("...")
                                ?
                                <TooltipTop
                                    title={data[attrName]}>
                                    <span>
                                        {driverDisplay(data[attrName])}
                                    </span>
                                </TooltipTop>
                                :
                                driverDisplay(data[attrName])
                            }
                        </Typography>
                    </ListItemText>
                    <ListItemText className={classes.kilometers}>
                        <Typography
                            variant="body1" className={classes.elementLabel}
                            onClick={() => onElementClick(data)}
                        >
                            {data.km}
                        </Typography>
                    </ListItemText>
                    <ListItemSecondaryAction>
                        <Typography variant="h5" className={classes.listItemTrophy}>
                            <img src={trophyIcons[data.trofeum]} className={classes.trophyIcon} />
                        </Typography>
                    </ListItemSecondaryAction>
                </ListItem>
            );
        }
    };


    const noCollecedTrophyRow = () => {
            return (
                <ListItem className={classes.disabledListItem} style={{ background: classes.disabledListHeader }}>
                    <ListItemText>
                        <Typography
                            variant="body1" className={classes.centeredText}
                        >
                            {t.collectingTrophiesIsOff}
                        </Typography>
                    </ListItemText>
                </ListItem>
            );
    };

    return (
        <Paper className={classes.container} square>
            <Typography variant="subtitle2" className={classes.title}>
                {title}
            </Typography>
            <List className={classes.list}>
                <ListItem className={data.trofea ? classes.listHeader: classes.disabledListHeader}>
                    <ListItemText className={isDrawerOpen && isLargeScreen ? classes.shorterDriverName : classes.driverName}>
                        <Typography variant="body2" style={{ fontWeight: 500 }}>
                            {headerLabel}
                        </Typography>
                    </ListItemText>
                    <ListItemText className={classes.kilometers}>
                        <Typography variant="body2" style={{ fontWeight: 500 }}>
                            {t.kilometers}
                        </Typography>
                    </ListItemText>
                    <ListItemSecondaryAction>
                        <Typography variant="body2" style={{ fontWeight: 500 }}>
                            {t.trophy}
                        </Typography>
                    </ListItemSecondaryAction>
                </ListItem>
                {data.trofea ?
                trail.map(({ x, height, ...rest }, index) => (
                    <animated.div
                        key={index}
                        className="trails-text"
                        style={{ ...rest, transform: x.interpolate(x => `translate3d(0,${x}px,0)`) }}
                    >
                        <animated.div style={{ height }}>{listOneRow(gradeData[index], theme, index)}</animated.div>
                    </animated.div>
                ))
                :
                trail.map(({ x, height, ...rest }, index) => (
                    <animated.div
                        key={index}
                        className="trails-text"
                        style={{ ...rest, transform: x.interpolate(x => `translate3d(0,${x}px,0)`)}}
                    >
                        <animated.div style={{ height }}>{noCollecedTrophyRow()}</animated.div>
                    </animated.div>
                ))
                }
            </List>
            <IconButton className={classes.helpIcon}
                onClick={handleTrophyLegendShow}
            >
                <HelpOutlineIcon />
            </IconButton>
            <TrophyLegendPopover
                onClose={() => setAnchor(null)}
                anchorEl={anchor}
            />
        </Paper>
    );
}


const mapStateToProps = state => {
    return {
        isDrawerOpen: state.requestReducer.isDrawerOpen
    };
};
export default connect(mapStateToProps)(TrophyTopList);

TrophyTopList.propTypes = {
    title: PropTypes.string,
    data: PropTypes.object,
    attrName: PropTypes.string.isRequired,
    headerLabel: PropTypes.string,
    onElementClick: PropTypes.func,
    //redux
    isDrawerOpen: PropTypes.bool,
};